import React, { useEffect, useState } from 'react';
import {
  Box, Heading, Button, Table, Thead, Tbody, Tr, Th, Td, HStack, useToast, Spinner, Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton, FormControl, FormLabel, Input, NumberInput, NumberInputField, Switch, Flex, Text, IconButton, SimpleGrid
} from '@chakra-ui/react';
import { AddIcon, EditIcon, DeleteIcon } from '@chakra-ui/icons';
import { supabase } from '../../services/supabaseClient';
import AdminLayout from './AdminLayout';

const initialForm = { nome: '', valor: '', tempo_estimado: '', ativo: true };

const ServicosPage = () => {
  const [servicos, setServicos] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const toast = useToast();

  const fetchServicos = async () => {
    setLoading(true);
    const { data, error } = await supabase.from('servicos').select('*').order('criado_em', { ascending: false });
    if (!error) setServicos(data);
    setLoading(false);
  };

  useEffect(() => { fetchServicos(); }, []);

  const openModal = (servico = null) => {
    if (servico) {
      setForm({
        nome: servico.nome,
        valor: servico.valor,
        tempo_estimado: servico.tempo_estimado,
        ativo: servico.ativo
      });
      setEditingId(servico.id);
    } else {
      setForm(initialForm);
      setEditingId(null);
    }
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setForm(initialForm);
    setEditingId(null);
  };

  const handleSave = async () => {
    if (!form.nome || !form.valor || !form.tempo_estimado) {
      toast({ title: 'Preencha todos os campos obrigatórios', status: 'warning' });
      return;
    }
    setLoading(true);
    let result;
    if (editingId) {
      result = await supabase.from('servicos').update(form).eq('id', editingId);
    } else {
      result = await supabase.from('servicos').insert([form]);
    }
    setLoading(false);
    if (!result.error) {
      toast({ title: editingId ? 'Serviço atualizado!' : 'Serviço cadastrado!', status: 'success' });
      fetchServicos();
      closeModal();
    } else {
      toast({ title: 'Erro ao salvar serviço', description: result.error.message, status: 'error' });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este serviço?')) return;
    const { error } = await supabase.from('servicos').delete().eq('id', id);
    if (!error) {
      toast({ title: 'Serviço excluído', status: 'info' });
      fetchServicos();
    } else {
      toast({ title: 'Erro ao excluir', description: error.message, status: 'error' });
    }
  };

  return (
    <AdminLayout>
      <Box mt={{ base: 6, md: 0 }}>
        <HStack justify="space-between" mb={8}>
          <Heading size="lg">Serviços</Heading>
          <Button leftIcon={<AddIcon />} colorScheme="yellow" bg="yellow.400" color="black" _hover={{ bg: 'yellow.500' }} onClick={() => openModal()}>Adicionar Serviço</Button>
        </HStack>
        {loading ? <Spinner /> : (
          <Box>
            {/* Tabela Desktop */}
            <Box display={{ base: 'none', md: 'block' }} overflowX="auto">
              <Table variant="striped" colorScheme="blackAlpha">
                <Thead>
                  <Tr>
                    <Th>Nome</Th>
                    <Th>Valor (R$)</Th>
                    <Th>Tempo (min)</Th>
                    <Th>Ativo</Th>
                    <Th>Ações</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {servicos.map(s => (
                    <Tr key={s.id}>
                      <Td>{s.nome}</Td>
                      <Td>{Number(s.valor).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</Td>
                      <Td>{s.tempo_estimado} min</Td>
                      <Td>{s.ativo ? 'Sim' : 'Não'}</Td>
                      <Td>
                        <HStack>
                          <Button size="sm" colorScheme="yellow" variant="outline" onClick={() => openModal(s)}>Editar</Button>
                          <Button size="sm" colorScheme="red" variant="outline" onClick={() => handleDelete(s.id)}>Excluir</Button>
                        </HStack>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
            {/* Cards Mobile */}
            <Box display={{ base: 'block', md: 'none' }}>
              {servicos.map(s => (
                <Box key={s.id} borderWidth={1} borderRadius="lg" p={4} mb={4} bg="gray.900" color="white" boxShadow="md">
                  <Flex justify="space-between" align="center" mb={2}>
                    <Text fontWeight="bold" fontSize="lg">{s.nome}</Text>
                    <HStack>
                      <IconButton icon={<EditIcon />} aria-label="Editar" size="sm" colorScheme="yellow" bg="yellow.400" color="black" _hover={{ bg: 'yellow.500' }} onClick={() => openModal(s)} />
                      <IconButton icon={<DeleteIcon />} aria-label="Excluir" size="sm" colorScheme="yellow" bg="yellow.400" color="black" _hover={{ bg: 'yellow.500' }} onClick={() => handleDelete(s.id)} />
                    </HStack>
                  </Flex>
                  <Text fontSize="sm"><b>Valor:</b> {Number(s.valor).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</Text>
                  <Text fontSize="sm"><b>Tempo:</b> {s.tempo_estimado} min</Text>
                  <Text fontSize="sm"><b>Status:</b> {s.ativo ? 'Ativo' : 'Inativo'}</Text>
                </Box>
              ))}
            </Box>
          </Box>
        )}
        {/* Modal de cadastro/edição */}
        <Modal isOpen={modalOpen} onClose={closeModal} size={{ base: "md", md: "lg" }} isCentered>
          <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(5px)" />
          <ModalContent 
            bg="gray.800" 
            color="white" 
            borderRadius="xl" 
            boxShadow="xl"
            mx={{ base: 3, md: 0 }}
            my={{ base: 3, md: 0 }}
            maxH={{ base: "calc(100vh - 80px)", md: "auto" }}
            overflowY="auto"
          >
            <ModalHeader borderBottomWidth="1px" borderColor="gray.700" py={4} px={6}>
              <Flex align="center">
                {editingId ? (
                  <EditIcon color="yellow.300" mr={3} />
                ) : (
                  <AddIcon color="yellow.300" mr={3} />
                )}
                <Text fontSize="xl" fontWeight="bold" color="white">
                  {editingId ? 'Editar Serviço' : 'Novo Serviço'}
                </Text>
              </Flex>
            </ModalHeader>
            <ModalCloseButton color="white" />
            <ModalBody py={6} px={{ base: 4, md: 6 }}>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} mb={6}>
                <FormControl isRequired>
                  <FormLabel fontWeight="medium" color="whiteAlpha.900">Nome do Serviço</FormLabel>
                  <Input 
                    value={form.nome} 
                    onChange={e => setForm({ ...form, nome: e.target.value })} 
                    bg="gray.700" 
                    color="white" 
                    borderColor="gray.600"
                    _hover={{ borderColor: "yellow.300" }}
                    _focus={{ borderColor: "yellow.300", boxShadow: "0 0 0 1px var(--chakra-colors-yellow-300)" }}
                    placeholder="Ex: Corte de Cabelo"
                  />
                </FormControl>
                
                <FormControl isRequired>
                  <FormLabel fontWeight="medium" color="whiteAlpha.900">Valor (R$)</FormLabel>
                  <NumberInput 
                    min={0} 
                    precision={2} 
                    value={form.valor} 
                    onChange={(_, v) => setForm({ ...form, valor: v })}
                  >
                    <NumberInputField 
                      bg="gray.700" 
                      color="white" 
                      borderColor="gray.600"
                      _hover={{ borderColor: "yellow.300" }}
                      _focus={{ borderColor: "yellow.300", boxShadow: "0 0 0 1px var(--chakra-colors-yellow-300)" }}
                      placeholder="0,00"
                    />
                  </NumberInput>
                  {form.valor && (
                    <Text fontSize="sm" color="yellow.300" mt={1}>
                      {Number(form.valor).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                    </Text>
                  )}
                </FormControl>
              </SimpleGrid>
              
              <FormControl isRequired mb={5}>
                <FormLabel fontWeight="medium" color="whiteAlpha.900">Tempo Estimado (min)</FormLabel>
                <NumberInput 
                  min={1} 
                  value={form.tempo_estimado} 
                  onChange={(_, v) => setForm({ ...form, tempo_estimado: v })}
                >
                  <NumberInputField 
                    bg="gray.700" 
                    color="white" 
                    borderColor="gray.600"
                    _hover={{ borderColor: "yellow.300" }}
                    _focus={{ borderColor: "yellow.300", boxShadow: "0 0 0 1px var(--chakra-colors-yellow-300)" }}
                    placeholder="Tempo em minutos"
                  />
                </NumberInput>
              </FormControl>
              
              <FormControl display="flex" alignItems="center">
                <Switch 
                  id="servico-ativo" 
                  isChecked={form.ativo} 
                  onChange={e => setForm({ ...form, ativo: e.target.checked })} 
                  colorScheme="yellow" 
                  size="lg"
                  mr={3}
                />
                <FormLabel htmlFor="servico-ativo" mb={0} color="whiteAlpha.900" cursor="pointer">
                  Serviço ativo para agendamento
                </FormLabel>
              </FormControl>
            </ModalBody>
            
            <ModalFooter borderTopWidth="1px" borderColor="gray.700" py={4} px={6}>
              <Flex justify="space-between" w="100%" direction={{ base: 'column', sm: 'row' }} gap={3}>
                <Button 
                  variant="outline" 
                  onClick={closeModal} 
                  color="white" 
                  borderColor="gray.600"
                  _hover={{ bg: "gray.700" }}
                  width={{ base: "100%", sm: "auto" }}
                >
                  Cancelar
                </Button>
                <Button 
                  colorScheme="yellow" 
                  onClick={handleSave} 
                  isLoading={loading}
                  leftIcon={editingId ? <EditIcon /> : <AddIcon />}
                  width={{ base: "100%", sm: "auto" }}
                  _hover={{ bg: "yellow.500" }}
                >
                  {editingId ? 'Atualizar Serviço' : 'Salvar Serviço'}
                </Button>
              </Flex>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </Box>
    </AdminLayout>
  );
};

export default ServicosPage;
